import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@mui/material';
import SelectInput from '@/components/forms/SelectInput';
import Styles from '@/assets/js/Styles';
import { useController } from 'react-hook-form';
import { noop } from '@/utils/functions';
import Container from '../../../../components/Container';
import { BlockPath, useCurrentBlock } from '../../CurrentBlockProvider';
import { BlockComponent } from '../../../lib/declarations/EditorComponentTypes';
import { useControlledFieldArray } from '../../../../hooks/useControlledFieldArray';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';
import M24PageFinder from '../../../../components/Finder/M24PageFinder/M24PageFinder';
import PageListManualBlockItem from './PageListManualBlockItem';
import { EventsManualBlock as EventsManualBlockModel } from '../../../../declarations/models/blocks/EventsBlocks';
import EventsDisplayOptions from './EventsDisplayOptions';
import { OutlinedContainer } from '../../../../components/OutlinedContainer';

export const EventsManualBlock: BlockComponent = () => {
  const { blockPath, block } = useCurrentBlock();
  const { t } = useTranslation('components');
  const [pageFinderOpen, setPageFinderOpen] = useState<boolean>(false);
  const [normalPageFinderOpen, setNormalPageFinderOpen] = useState<boolean>(false);

  const currentBlock = block as EventsManualBlockModel;

  const {
    fields: items,
    append,
    move,
    remove,
  } = useControlledFieldArray<BlockSpecificPage<EventsManualBlockModel>, `${BlockPath}.items`>({
    name: `${blockPath}.items`,
  });

  const {
    field: { value: showAllLinkPage, onChange: onChangeShowAllLinkPage },
  } = useController<BlockSpecificPage<EventsManualBlockModel>, `${BlockPath}.showAllLinkPage`>({
    name: `${blockPath}.showAllLinkPage`,
  });

  const gridSizeOptions = ['auto', '3', '4', '5', '6'];
  const mobileGridOptions = ['auto', 'list', 'horizontal', 'card'];
  const mobileListOptions = ['auto', 'card'];

  return (
    <Container fullWidth column mt={1} gap={2} left>
      <M24PageFinder
        open={pageFinderOpen}
        onClose={() => setPageFinderOpen(false)}
        onSelectionConfirmed={(pages) => {
          pages.forEach((page) => {
            if (page.source.id) {
              const alreadyExists = items.some((item) => item.id === page.source.id);
              if (!alreadyExists) {
                append({ id: page.source.id });
              }
            }
          });
        }}
        multiSelect
        isEvent
      />

      <M24PageFinder
        hideSiteSelector
        open={normalPageFinderOpen}
        onClose={() => setNormalPageFinderOpen(false)}
        onSelectionConfirmed={(pages) => {
          if (pages.length > 0) {
            onChangeShowAllLinkPage(pages[0].source);
          }
        }}
      />
      <OutlinedContainer
        fullWidth
        column
        left
        label={t('EventsBlock.EventsLabel')}
        sx={{
          backgroundColor: Styles.Colors.FIXED_SECTION_BACKGROUND_COLOR,
        }}>
        {items?.length === 0 && (
          <Typography sx={{ color: Styles.Colors.DARK_GREY, textAlign: 'center', pt: 1 }} variant='body2'>
            <i>{t('EventsBlock.NoEventsSelected')}</i>
          </Typography>
        )}
        <Container fullWidth column gap={0}>
          {items?.map((item, index) => {
            return (
              <PageListManualBlockItem
                key={String(item.id) + index} // eslint-disable-line react/no-array-index-key
                index={index}
                onReorder={move}
                onDelete={remove}
                color={Styles.Colors.STRONG_GREEN_TRANSPARENT}
              />
            );
          })}
        </Container>
        <Button variant='contained' color='secondary' onClick={() => setPageFinderOpen(true)}>
          {t('EventsBlock.FinderButton')}
        </Button>
      </OutlinedContainer>

      {!currentBlock.view?.includes('list') && (
        <OutlinedContainer fullWidth column left label={t('KPBlock.DisplayOptionsModuleLabel')}>
          <Container fullWidth left gap={2} mt={2} mb={2}>
            <SelectInput
              hideNoSelectionOption
              defaultValue='auto'
              options={gridSizeOptions}
              getOptionKey={(gridSize) => `size-${gridSize}`}
              getOptionLabel={(gridSize) => `${gridSize}`}
              path={`${blockPath}.gridSize`}
              label={t('CardModuleBlock.GridSizeLabel')}
            />

            <SelectInput
              hideNoSelectionOption
              defaultValue='auto'
              options={mobileGridOptions}
              getOptionKey={(o) => `mobileGrid-${o}`}
              getOptionLabel={(o) => t(`CardModuleViewTypeSelector.CardModuleViewTypeVariant.${o}`)}
              path={`${blockPath}.gridMobileBehaviour`}
              label={t('CardModuleBlock.GridMobileBehaviourLabel')}
            />
          </Container>
        </OutlinedContainer>
      )}

      {currentBlock.view?.includes('list') && (
        <Container fullWidth left gap={2} mt={2} mb={2}>
          <SelectInput
            hideNoSelectionOption
            defaultValue='auto'
            options={mobileListOptions}
            getOptionKey={(o) => `mobileGrid-${o}`}
            getOptionLabel={(o) => t(`CardModuleViewTypeSelector.CardModuleViewTypeVariant.${o}`)}
            path={`${blockPath}.gridMobileBehaviour`}
            label={t('CardModuleBlock.ListMobileBehaviourLabel')}
          />
        </Container>
      )}

      <EventsDisplayOptions blockPath={blockPath} />

      <OutlinedContainer fullWidth column left label={t('EventsBlock.ShowAllLinkPageTitle')}>
        {showAllLinkPage && (
          <PageListManualBlockItem
            index={0}
            onReorder={noop}
            onDelete={() => onChangeShowAllLinkPage(null)}
            overrideItemPath={`${blockPath}.showAllLinkPage`}
            color={Styles.Colors.LIGHT_GREY}
            hideHighlightCheckbox
            hideSkinSelector
            disableReordering
          />
        )}
        <Button onClick={() => setNormalPageFinderOpen(true)} variant='outlined' color='secondary'>
          {t('EventsBlock.ShowAllLinkPageButtonLabel')}
        </Button>
      </OutlinedContainer>
    </Container>
  );
};

export default EventsManualBlock;
